
/*
  THE KITCHEN BOUTIQUE
  Dynamic Cross-Brand Upsell Popup

  Coke -> Pepsi
  Pepsi -> Coke
  Dr Pepper -> Coke

  Matches exact cup size and pack quantity.
  Uses real Shopify variant images, prices, and weights.
  Only offers packs within the same shipping tier.
  Appears once per shopping session.
*/

(() => {
  'use strict';

  const SESSION_KEY = 'kb-cross-sell-shown';

  const GRAMS_PER_LB = 453.59237;
  const PACKAGING_GRAMS = 0.1 * GRAMS_PER_LB;

  const SHIPPING_TIERS = [
    { max: 6.44, price: 6.99 },
    { max: 8.41, price: 10.99 },
    { max: 14.70, price: 15.99 },
    { max: 21.15, price: 23.99 },
    { max: 27.60, price: 31.99 }
  ];

  const ELIGIBLE_HANDLES = [
    'coca-cola-pepsi-restaurant-cups',
    'dr-pepper-restaurant-cups',
    'reusable-plastic-restaurant-cups'
  ];

  let catalog = [];
  let selectedVariant = null;
  let isAdding = false;
  let offerPending = false;

  const $ = id => document.getElementById(id);

  const root = window.Shopify?.routes?.root || '/';

  function url(path) {
    return root.replace(/\/$/, '') + '/' + path;
  }

  // -----------------------------
  // SHOPIFY CART
  // -----------------------------

  async function getCart() {
    const response = await fetch(url('cart.js'), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to retrieve Shopify cart');
    }

    return response.json();
  }

  // -----------------------------
  // SHIPPING CALCULATIONS
  // -----------------------------

  function getShippingTier(weightGrams) {
    return SHIPPING_TIERS.findIndex(tier =>
      weightGrams <= tier.max * GRAMS_PER_LB
    );
  }

  async function shippingUnchanged(variant) {
    const cart = await getCart();

    // Only apply the offer to qualifying cup orders.
    const hasOtherPhysicalProducts = cart.items.some(item =>
      item.requires_shipping &&
      !ELIGIBLE_HANDLES.includes(item.handle)
    );

    if (hasOtherPhysicalProducts) {
      return false;
    }

    // Calculate current cart weight.
    const currentWeight = cart.items.reduce(
      (total, item) =>
        total + Number(item.grams || 0) * item.quantity,
      PACKAGING_GRAMS
    );

    const additionalWeight = Number(variant.weight);

    if (!(additionalWeight > 0)) {
      return false;
    }

    // Check inventory if Shopify tracks it.
    if (
      variant.inventory_management &&
      variant.inventory_policy === 'deny' &&
      variant.inventory_quantity != null
    ) {
      const quantityInCart = cart.items
        .filter(item =>
          String(item.variant_id) === String(variant.id)
        )
        .reduce(
          (sum, item) => sum + item.quantity,
          0
        );

      if (
        quantityInCart + 1 >
        variant.inventory_quantity
      ) {
        return false;
      }
    }

    const currentTier = getShippingTier(currentWeight);

    const newTier = getShippingTier(
      currentWeight + additionalWeight
    );

    // Never promise unchanged shipping beyond our tiers.
    if (currentTier < 0 || newTier < 0) {
      return false;
    }

    return currentTier === newTier;
  }

  // -----------------------------
  // PRODUCT MATCHING
  // -----------------------------

  function findVariant(brand, size, pack) {
    for (const product of catalog) {
      const variant = product.variants.find(v =>
        v.brand === brand &&
        v.size === size &&
        v.pack === pack &&
        v.available &&
        Number(v.weight) > 0
      );

      if (variant) {
        return { product, variant };
      }
    }

    return null;
  }

  function findSourceVariant(variantId) {
    for (const product of catalog) {
      const variant = product.variants.find(
        v => String(v.id) === String(variantId)
      );

      if (variant) {
        return { product, variant };
      }
    }

    return null;
  }

  function getRecommendedBrand(product, variant) {
    if (
      product.handle ===
      'coca-cola-pepsi-restaurant-cups'
    ) {
      if (variant.brand === 'Pepsi') {
        return 'Coke';
      }

      if (variant.brand === 'Coke') {
        return 'Pepsi';
      }
    }

    if (
      product.handle ===
      'dr-pepper-restaurant-cups'
    ) {
      return 'Coke';
    }

    return null;
  }

  // -----------------------------
  // PRICE FORMATTING
  // -----------------------------

  function formatMoney(cents) {
    const currency =
      window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(Number(cents) / 100);
  }

  // -----------------------------
  // DISPLAY THE POPUP
  // -----------------------------

  async function showOffer(brand, size, pack) {
    const match = findVariant(brand, size, pack);

    if (!match) {
      console.info(
        '[KB] No matching variation available:',
        brand, size, pack
      );
      return false;
    }

    const { product, variant } = match;

    const eligible = await shippingUnchanged(variant);

    if (!eligible) {
      console.info(
        '[KB] Upsell skipped: shipping or inventory check',
        variant.title
      );
      return false;
    }

    const popup = $('kb-cross-sell-popup');

    if (!popup || popup.open) {
      return false;
    }

    selectedVariant = variant;

    const cupCount = parseInt(pack, 10);

    const displayBrand =
      brand === 'Coke' ? 'Coca-Cola' : brand;

    // Exact offer headline.
    $('kb-cross-sell-offer').textContent =
      `${cupCount} ${displayBrand} ${size} cups`;

    // Exact Shopify product and variation.
    $('kb-cross-sell-name').textContent =
      product.title;

    $('kb-cross-sell-variation').textContent =
      variant.title;

    $('kb-cross-sell-price').textContent =
      formatMoney(variant.price);

    // Exact variation thumbnail.
    const image = $('kb-cross-sell-image');

    if (variant.image) {
      image.src = variant.image;
      image.alt = variant.title;
      image.hidden = false;
    } else {
      image.hidden = true;
    }

    // Add button.
    const button = $('kb-cross-sell-add');

    button.disabled = false;
    button.textContent =
      `Add ${cupCount} cups to my order`;

    popup.showModal();

    console.info(
      '[KB] Cross-sell displayed:',
      variant.title
    );

    return true;
  }

  // -----------------------------
  // ADD OFFER TO SHOPIFY CART
  // -----------------------------

  async function addOffer() {
    if (!selectedVariant || isAdding) {
      return;
    }

    const button = $('kb-cross-sell-add');

    isAdding = true;
    button.disabled = true;
    button.textContent = 'Adding cups...';

    try {
      // Recheck shipping and inventory before adding.
      const eligible = await shippingUnchanged(
        selectedVariant
      );

      if (!eligible) {
        throw new Error(
          'This pack no longer fits within your current shipping tier or is unavailable.'
        );
      }

      const response = await fetch(url('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: selectedVariant.id,
            quantity: 1
          }]
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Unable to add this variation.'
        );
      }

      // Mark offer as used.
      sessionStorage.setItem(SESSION_KEY, 'true');

      $('kb-cross-sell-popup').close();

      // Show updated Shopify cart.
      window.location.assign(url('cart'));

    } catch (error) {
      console.error('[KB] Add to cart error:', error);

      button.textContent = 'Try again';

      alert(
        error.message ||
        'Unable to add cups. Please try again.'
      );

    } finally {
      isAdding = false;
      button.disabled = false;
    }
  }

  // -----------------------------
  // AUTOMATIC ADD-TO-CART TRIGGER
  // -----------------------------

  async function handleProductAdded(event) {
    if (offerPending) return;

    // Show only once per shopping session.
    if (
      sessionStorage.getItem(SESSION_KEY) === 'true'
    ) {
      return;
    }

    const addedId = String(
      event.detail?.variantId || ''
    );

    if (!addedId) return;

    // Identify the original variation.
    const source = findSourceVariant(addedId);

    if (!source) return;

    const { product, variant } = source;

    // Coke -> Pepsi
    // Pepsi -> Coke
    // Dr Pepper -> Coke
    const targetBrand = getRecommendedBrand(
      product,
      variant
    );

    if (!targetBrand) return;

    offerPending = true;

    try {
      // Match original size and pack quantity.
      const opened = await showOffer(
        targetBrand,
        variant.size,
        variant.pack
      );

      if (opened) {
        sessionStorage.setItem(SESSION_KEY, 'true');

        console.info(
          '[KB] Automatic upsell opened:',
          targetBrand,
          variant.size,
          variant.pack
        );
      }

    } catch (error) {
      console.error(
        '[KB] Automatic upsell error:',
        error
      );

    } finally {
      offerPending = false;
    }
  }

  // -----------------------------
  // INITIALIZE POPUP
  // -----------------------------

  function initialize() {
    const dataElement = $('kb-cross-sell-catalog');
    const popup = $('kb-cross-sell-popup');

    if (!dataElement || !popup) {
      console.error(
        '[KB] Popup HTML or product catalog missing'
      );
      return;
    }

    try {
      const data = JSON.parse(
        dataElement.textContent
      );

      catalog = data.products || [];

    } catch (error) {
      console.error(
        '[KB] Unable to parse product catalog:',
        error
      );
      return;
    }

    // Add to order button.
    $('kb-cross-sell-add').addEventListener(
      'click',
      addOffer
    );

    // Close and decline buttons.
    popup.querySelectorAll(
      '[data-kb-close]'
    ).forEach(button => {
      button.addEventListener('click', () => {
        popup.close();
      });
    });

    // Listen for successful Shopify product addition.
    window.addEventListener(
      'kb:product-added',
      handleProductAdded
    );

    // Preserve manual testing functions.
    window.KBCrossSell = {
      showTest: () =>
        showOffer('Coke', '20oz', '4 Cups'),

      showOffer
    };

    console.info(
      '[KB] Cross-sell popup initialized successfully'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
